import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';

import { apiService, ChatSearchResponse, ChatSearchResult } from '../services/apiService';
import SearchResultItem from '../components/search/SearchResultItem';
import SearchFilters from '../components/search/SearchFilters';
import SearchPagination from '../components/search/SearchPagination';

const SearchResults: React.FC = () => {
  const { query } = useParams<{ query: string }>();
  const navigate = useNavigate();
  
  const [searchResults, setSearchResults] = useState<ChatSearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    limit: 20,
    messageId: undefined as string | undefined
  });

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query, filters]);

  const performSearch = async (searchQuery: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.searchChats(
        searchQuery, 
        filters.limit, 
        filters.messageId
      );
      
      if (response.success && response.data) {
        setSearchResults(response.data);
      } else {
        setError(response.error || 'Search failed');
      }
    } catch (err) {
      setError('An error occurred while searching');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const handleResultClick = (result: ChatSearchResult) => {
    navigate(`/channels/${result.chat_id}?messageId=${result.message.id}`);
  };

  const handleNewSearch = (newQuery: string) => {
    navigate(`/search/${encodeURIComponent(newQuery)}`);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Search Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Search Results
          </h1>
          <p className="text-gray-600">
            {query ? `Results for "${query}"` : 'Enter a search query'}
          </p>
        </div>
      </div>

      {/* Search Filters Top Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-6xl mx-auto">
          <SearchFilters 
            filters={filters}
            onFilterChange={handleFilterChange}
            onNewSearch={handleNewSearch}
            currentQuery={query || ''}
          />
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
            {loading && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Searching...</p>
                </div>
              </div>
            )}

            {error && (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-red-600 mb-4">{error}</p>
                  <button
                    onClick={() => query && performSearch(query)}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {searchResults && !loading && (
              <>
                {/* Results Header */}
                <div className="bg-white border-b border-gray-200 px-6 py-4">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-600">
                      {searchResults.total_results} result{searchResults.total_results !== 1 ? 's' : ''} found
                    </p>
                  </div>
                </div>

                {/* Results List */}
                <div className="flex-1 overflow-y-auto">
                  <div className="max-w-4xl mx-auto p-6">
                    {searchResults.results.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-gray-500 text-lg">No results found</p>
                        <p className="text-gray-400 mt-2">Try adjusting your search terms or filters</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {searchResults.results.map((result, index) => (
                          <SearchResultItem
                            key={`${result.chat_id}-${result.message.id}`}
                            result={result}
                            query={searchResults.query}
                            onClick={() => handleResultClick(result)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pagination */}
                {searchResults.results.length > 0 && (
                  <div className="bg-white border-t border-gray-200 px-6 py-4">
                    <SearchPagination 
                      totalResults={searchResults.total_results}
                      currentLimit={filters.limit}
                      onLimitChange={(limit) => handleFilterChange({ limit })}
                    />
                  </div>
                )}
              </>
            )}
      </div>
    </div>
  );
};

export default SearchResults;
